import ExpandableNotification from "components/Notification/ExpandableNotification";
import NotificationService from "components/Notification/Notification.service";
import RecordTable from "components/Table/RecordTable";
import { savepointsListingColumns } from "config";
import { executionSavepointListSubscription } from "containers/Executions/Executions.type";
import { actions as resourcesActions } from "containers/Resources/Resources.reducer";
import { getSavepointListData } from "containers/Resources/Resources.selectors";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReconnectingWebSocket from "reconnecting-websocket";
import { useVisibilityTabChange } from "utils/useVisibilityTabChange";
import { initialQuery } from "./constants";
import "./SavepointsListing.scss";

const SAVEPOINT_LIST_IRI_PREFIX = "/execution/";
const getSavepointListIri = (executionId) =>
    executionId ? `${SAVEPOINT_LIST_IRI_PREFIX}${executionId}/savepoint-list` : null;

const SavepointsListing = ({ execution }) => {
    const executionId = execution?.id;
    const dispatch = useDispatch();

    const [queryInfo, setQueryInfo] = useState(initialQuery);
    const [isLoading, setLoading] = useState(false);

    const savepointDataSelector = (state) => getSavepointListData(state, executionId);
    const savepointData = useSelector(savepointDataSelector);
    const dumpListPayload =
        savepointData?.execution?.execution_dump_list_execution_id ??
        savepointData?.data?.execution?.execution_dump_list_execution_id;
    const savepointList = dumpListPayload?.execution_dump_list ?? [];
    const savepointCount = dumpListPayload?.execution_dump_count ?? 0;

    const { closeAllWebsocketConnections } = useVisibilityTabChange();

    const updateQuery = useCallback(
        (newQuery) => {
            setQueryInfo((prev) => ({ ...prev, ...newQuery }));
        },
        []
    );

    useEffect(() => {
        if (!executionId) return;

        const typedWindow = typeof window !== "undefined" ? window : {};
        const websocketHost =
            typedWindow.REACT_APP_WEBSOCKET_API || `wss://${typedWindow.location?.host}`;
        const namespace = typedWindow.REACT_APP_WEBSOCKET_API_NAMESPACE || "";
        let socket = null;

        const performFetch = () => {
            setLoading(true);
            if (socket) {
                socket.close();
            }
            const websocketParams = {
                subscription: executionSavepointListSubscription,
                variables: {
                    id: executionId,
                    limit: queryInfo.limit,
                    offset: queryInfo.offset,
                    order: queryInfo.order,
                },
            };
            // Keep GraphQL out of the URL (CSRF + log leakage): connect to a
            // clean endpoint and send the subscription in the first websocket
            // message. `variables` stays an object, matching POST /graphql and
            // the other first-message subscriptions.
            const url = `${websocketHost}${namespace}/graphql?subscription_message=true`;
            socket = new ReconnectingWebSocket(url);
            socket.addEventListener("open", () => {
                socket.send(JSON.stringify(websocketParams));
            });
            socket.addEventListener("message", (event) => {
                try {
                    const response = JSON.parse(event.data);
                    if (response.BadRequest) {
                        const { title, message } = response.BadRequest;
                        NotificationService.error(
                            <ExpandableNotification title={title} details={message} />
                        );
                        if (socket) socket.close();
                    } else {
                        dispatch({
                            type: resourcesActions.STORE_RESOURCE_DATA,
                            iri: getSavepointListIri(executionId),
                            response,
                        });
                    }
                } finally {
                    setLoading(false);
                }
            });
        };

        if (!closeAllWebsocketConnections) {
            performFetch();
        }

        return () => {
            if (socket) socket.close();
        };
    }, [
        dispatch,
        executionId,
        queryInfo.limit,
        queryInfo.offset,
        queryInfo.order,
        closeAllWebsocketConnections,
    ]);

    const resourceList = savepointList.map((savepoint) => ({
        ...savepoint,
        executionId,
        load_savepoint: savepoint?.id,
        resource_type: "savepoint",
    }));

    return (
        <>
            {execution && (
                <div className="savepointsListingListHeader">
                    <div className="savepointsListingSpacer" />
                </div>
            )}
            <RecordTable
                columns={savepointsListingColumns}
                data={resourceList}
                totalItemCount={savepointCount}
                recordTypes="execution"
                isLoading={isLoading}
                updateQuery={updateQuery}
                initialQuery={initialQuery}
                options={{
                    toolbar: "none",
                    toolbarFilters: false,
                    selectableRows: "none",
                }}
                tableSettingsKey="save_points"
            />
        </>
    );
};

SavepointsListing.defaultProps = {
    execution: null,
};

SavepointsListing.propTypes = {
    execution: PropTypes.object,
};

export default SavepointsListing;
